// Team member form component

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  VStack,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Box,
  Text,
  Checkbox,
  CheckboxGroup,
  FormErrorMessage,
  Radio,
  RadioGroup,
  SimpleGrid,
} from '@chakra-ui/react';
import { lockedPhoneTeamMemberSchema, teamMemberSchema, TeamMemberFormData, TeamMemberFormInput } from '@/client/utils/validationSchemas';
import { CountryCodeSelect } from '@/design-system/components/CountryCodeSelect';
import { FormField } from '@/components/Modal/FormField';
import { useAuth } from '@/lib/sdk/auth/hooks';
import { FeaturePermissions } from '@/lib/sdk/auth/types';
import { FeatureAccess } from '@/lib/sdk/organization/types';
import type { InstantTaskScope, TeamMember } from '@/lib/sdk/team/types';

interface TeamFormProps {
  initialData?: Partial<TeamMemberFormData>;
  onSubmit: (data: TeamMemberFormData) => Promise<void>;
  onCancel?: () => void;
  isCurrentUserAdmin?: boolean;
  /** The member has more rights than the current non-admin, who can't change their phone. */
  phoneLocked?: boolean;
  /** Org members selectable under "Can view tasks of" (excludes the member being edited). */
  memberOptions?: TeamMember[];
}

const FEATURE_ACTIONS: Record<string, { label: string; actions: { value: string; label: string }[] }> = {
  reports: {
    label: 'Reports',
    actions: [
      { value: 'view', label: 'View' },
      { value: 'add', label: 'Add' },
      { value: 'edit', label: 'Edit' },
      { value: 'delete', label: 'Delete' },
      { value: 'share', label: 'Share' },
    ],
  },
  team: {
    label: 'Team',
    actions: [
      { value: 'view', label: 'View' },
      { value: 'add', label: 'Add' },
      { value: 'edit', label: 'Edit' },
      { value: 'delete', label: 'Delete' },
    ],
  },
  workflows: {
    label: 'Workflows & Instant Tasks',
    actions: [
      { value: 'view_instant', label: 'View Instant Tasks' },
      { value: 'edit_data', label: 'Edit Data' },
      { value: 'add_data', label: 'Add Data' },
      { value: 'add', label: 'Add' },
      { value: 'edit', label: 'Edit' },
      { value: 'delete', label: 'Delete' },
      { value: 'ai_assist', label: 'AI Assistant' },
    ],
  },
  master_data: {
    label: 'Master Data',
    actions: [
      { value: 'view', label: 'View' },
      { value: 'add', label: 'Add' },
      { value: 'edit', label: 'Edit' },
      { value: 'delete', label: 'Delete' },
      { value: 'add_fields', label: 'Add Fields' },
      { value: 'edit_fields', label: 'Edit Fields' },
      { value: 'delete_fields', label: 'Delete Fields' },
      { value: 'add_categories', label: 'Add Categories' },
      { value: 'edit_categories', label: 'Edit Categories' },
      { value: 'delete_categories', label: 'Delete Categories' },
      { value: 'import', label: 'Import' },
      { value: 'export', label: 'Export' },
    ],
  },
  ai_chat: {
    label: 'AI Chat',
    actions: [
      { value: 'use', label: 'Use' },
    ],
  },
  nudges: {
    label: 'Reminders',
    actions: [
      { value: 'view', label: 'View' },
      { value: 'add', label: 'Add' },
      { value: 'edit', label: 'Edit' },
      { value: 'delete', label: 'Delete' },
    ],
  },
};

function migrateLegacyWorkflowPermissions(perms: FeaturePermissions | undefined): FeaturePermissions {
  if (!perms) return {};
  const result = { ...perms };

  // Coerce old events permissions into workflows
  if ((perms as any).events) {
    const eventsActions = (perms as any).events as string[];
    const workflowActions = new Set(result.workflows as string[] ?? []);
    for (const a of eventsActions) {
      if (a === 'view') {
        workflowActions.add('view_instant');
      } else if (a === 'add' || a === 'edit' || a === 'delete') {
        workflowActions.add(a);
      }
    }
    result.workflows = [...workflowActions] as any;
    delete (result as any).events;
  }

  // Strip stale view_data/view_templates — template viewing is ambient now
  // (#721), gated by org-level workflows access rather than a per-user action.
  if (result.workflows) {
    result.workflows = (result.workflows as string[]).filter(
      (a) => a !== 'view_data' && a !== 'view_templates'
    ) as any;
  }

  return result;
}

function migrateLegacyMasterDataActions(perms: FeaturePermissions | undefined): FeaturePermissions {
  if (!perms?.master_data) return perms ?? {};
  const expanded = new Set(perms.master_data as string[]);
  if (expanded.delete('manage_fields')) {
    (['add_fields', 'edit_fields', 'delete_fields'] as const).forEach((a) => expanded.add(a));
  }
  if (expanded.delete('manage_categories')) {
    (['add_categories', 'edit_categories', 'delete_categories'] as const).forEach((a) => expanded.add(a));
  }
  return { ...perms, master_data: [...expanded] as any };
}

export function TeamForm({ initialData, onSubmit, onCancel, isCurrentUserAdmin = false, phoneLocked = false, memberOptions = [] }: TeamFormProps) {
  const { currentOrg } = useAuth();
  const orgFeatureAccess = currentOrg?.feature_access || [];

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    setValue,
  } = useForm<TeamMemberFormInput, unknown, TeamMemberFormData>({
    resolver: zodResolver(phoneLocked ? lockedPhoneTeamMemberSchema : teamMemberSchema),
    defaultValues: {
      // A locked row keeps what's stored, not the org's default: the API accepts that as unchanged.
      country_code: phoneLocked ? initialData?.country_code ?? '' : initialData?.country_code || currentOrg?.default_country_code || '+91',
      phone_number: initialData?.phone_number || '',
      name: initialData?.name || '',
      org_code: initialData?.org_code || '',
      nickname: initialData?.nickname || '',
      adhoc_tasks_enabled: initialData?.adhoc_tasks_enabled ?? true,
      instant_task_scope: initialData?.instant_task_scope ?? 'all',
      instant_task_members: initialData?.instant_task_members ?? [],
      org_permissions: (initialData?.org_permissions === 'read' ? 'read_write' : initialData?.org_permissions) || 'read_write',
      feature_permissions: migrateLegacyMasterDataActions(migrateLegacyWorkflowPermissions(initialData?.feature_permissions)) || {},
    },
  });

  const watchedRole = watch('org_permissions');
  const watchedFeaturePermissions = watch('feature_permissions') || {};
  const watchedTaskScope = watch('instant_task_scope');
  const watchedTaskMembers = watch('instant_task_members');

  const ensureTeamViewForReports = (current: Record<string, string[]>) => {
    const reportsActions = current['reports'] || [];
    if (reportsActions.length > 0) {
      const teamActions = [...(current['team'] || [])];
      if (!teamActions.includes('view')) {
        teamActions.push('view');
        current['team'] = teamActions;
      }
    }
  };

  const handleFeatureActionToggle = (feature: string, action: string, checked: boolean) => {
    const current = { ...watchedFeaturePermissions } as Record<string, string[]>;
    const currentActions = [...(current[feature] || [])];

    if (checked) {
      if (!currentActions.includes(action)) {
        currentActions.push(action);
      }
    } else {
      const idx = currentActions.indexOf(action);
      if (idx > -1) {
        currentActions.splice(idx, 1);
      }
    }

    if (currentActions.length === 0) {
      delete current[feature];
    } else {
      current[feature] = currentActions;
    }

    // Dependency: any reports action requires team view
    if (feature === 'reports') {
      ensureTeamViewForReports(current);
    }

    setValue('feature_permissions', current as FeaturePermissions);
  };

  const handleSelectAllFeature = (feature: string, allActions: string[], checked: boolean) => {
    const current = { ...watchedFeaturePermissions } as Record<string, string[]>;

    if (checked) {
      current[feature] = [...allActions];
    } else {
      delete current[feature];
    }

    // Dependency: any reports action requires team view
    if (feature === 'reports' && checked) {
      ensureTeamViewForReports(current);
    }

    setValue('feature_permissions', current as FeaturePermissions);
  };

  // "Can view tasks of" is shown only in the permission editor's workflows group,
  // for a non-admin member granted View Instant Tasks.
  const showTaskVisibility =
    isCurrentUserAdmin &&
    watchedRole !== 'admin' &&
    orgFeatureAccess.includes('workflows') &&
    ((watchedFeaturePermissions.workflows ?? []) as string[]).includes('view_instant');

  const handleFormSubmit = async (data: TeamMemberFormData) => {
    // A hidden control leaves the stored setting unchanged.
    await onSubmit(showTaskVisibility ? data : { ...data, instant_task_scope: undefined, instant_task_members: undefined });
  };

  const onInvalidSubmit = (fieldErrors) => {
    console.error("Captured Validation Errors:", fieldErrors);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit, onInvalidSubmit)}>
      <VStack spacing={4} align="stretch">
        <FormControl isInvalid={!!errors.country_code}>
          <FormLabel>Country Code</FormLabel>
          <CountryCodeSelect
            value={watch('country_code')}
            onChange={(value) => setValue('country_code', value)}
            error={errors.country_code?.message}
            isDisabled={phoneLocked}
            testId="team-form-country-code"
          />
        </FormControl>

        {/* A fieldset, because react-hook-form drops the value of a disabled input. */}
        <Box as="fieldset" disabled={phoneLocked}>
          <FormField
            label="Phone Number"
            name="phone_number"
            register={register}
            error={errors.phone_number}
            type="tel"
            placeholder="Enter phone number"
            description={phoneLocked ? 'Only an admin can change the phone number of a member with more rights than you.' : undefined}
            required
            testId="team-form-phone"
          />
        </Box>

        <FormField
          label="Name"
          name="name"
          register={register}
          error={errors.name}
          placeholder="Enter full name"
          required
          testId="team-form-name"
        />

        <FormField
          label="Nickname"
          name="nickname"
          register={register}
          error={errors.nickname}
          placeholder="e.g. CK"
          description="Short alias used for @mention in WhatsApp (e.g. @CK). Leave blank to match by full name only."
          testId="team-form-nickname"
        />

        {/* Only an admin can change permissions (#1246). */}
        {isCurrentUserAdmin && (
          <FormField
            label="Permission Level"
            name="org_permissions"
            register={register}
            error={errors.org_permissions}
            type="select"
            options={[
              { value: 'read_write', label: 'User' },
              { value: 'admin', label: 'Administrator' },
            ]}
            required
            testId="team-form-permission"
          />
        )}

        <FormField
          label="Org Code"
          name="org_code"
          register={register}
          error={errors.org_code}
          placeholder="Enter org code"
          testId="team-form-org-code"
        />

        {/* Feature Permissions Editor - only shown when logged-in user is admin and target is not admin */}
        {isCurrentUserAdmin && watchedRole !== 'admin' && orgFeatureAccess.length > 0 && (
          <Box>
            <Text fontWeight="medium" mb={3} color="text-primary">
              Feature Permissions
            </Text>
            <Text fontSize="sm" color="text-secondary" mb={4}>
              Configure which features and actions this user can access.
            </Text>
            <VStack spacing={4} align="stretch">
              {orgFeatureAccess.map((feature: FeatureAccess) => {
                const config = FEATURE_ACTIONS[feature];
                if (!config) return null;
                const featureKey = feature as keyof FeaturePermissions;
                const currentActions = (watchedFeaturePermissions[featureKey] || []) as string[];
                const allActions = config.actions.map((a) => a.value);
                const allChecked = allActions.every((a) => currentActions.includes(a));

                return (
                  <Box
                    key={feature}
                    data-testid={`team-feature-group-${feature}`}
                    p={3}
                    borderWidth="1px"
                    borderColor="border-default"
                    borderRadius="md"
                    bg="bg-surface"
                  >
                    <HStack justify="space-between" mb={2}>
                      <Text fontWeight="medium" fontSize="sm" color="text-primary">
                        {config.label}
                      </Text>
                      <Checkbox
                        size="sm"
                        isChecked={allChecked}
                        isIndeterminate={currentActions.length > 0 && !allChecked}
                        onChange={(e) =>
                          handleSelectAllFeature(feature, allActions, e.target.checked)
                        }
                      >
                        <Text fontSize="xs" color="text-secondary">All</Text>
                      </Checkbox>
                    </HStack>
                    <SimpleGrid columns={{ base: 2, md: 3 }} spacing={2}>
                      {config.actions.map((action) => (
                        <Checkbox
                          key={action.value}
                          size="sm"
                          isChecked={currentActions.includes(action.value)}
                          onChange={(e) =>
                            handleFeatureActionToggle(feature, action.value, e.target.checked)
                          }
                        >
                          <Text fontSize="sm">{action.label}</Text>
                        </Checkbox>
                      ))}
                    </SimpleGrid>
                    {feature === 'workflows' && showTaskVisibility && (
                      <FormControl mt={3} isInvalid={!!errors.instant_task_members} data-testid="team-instant-task-visibility">
                        <FormLabel fontSize="sm" color="text-primary">Can view tasks of</FormLabel>
                        <RadioGroup
                          value={watchedTaskScope}
                          onChange={(value) => setValue('instant_task_scope', value as InstantTaskScope)}
                        >
                          <HStack spacing={4} wrap="wrap">
                            <Radio value="all" size="sm"><Text fontSize="sm" data-testid="team-visibility-all">Everyone</Text></Radio>
                            <Radio value="self" size="sm"><Text fontSize="sm" data-testid="team-visibility-self">Only their own</Text></Radio>
                            <Radio value="members" size="sm"><Text fontSize="sm" data-testid="team-visibility-members">Selected members</Text></Radio>
                          </HStack>
                        </RadioGroup>
                        {watchedTaskScope === 'members' && (
                          <Box mt={2} maxH="200px" overflowY="auto" borderWidth="1px" borderColor="border-default" borderRadius="md" p={2}>
                            <CheckboxGroup
                              value={watchedTaskMembers}
                              onChange={(values) => setValue('instant_task_members', values as string[], { shouldValidate: true })}
                            >
                              <VStack align="stretch" spacing={1}>
                                {memberOptions.map((member) => (
                                  <Checkbox key={member.id} value={member.id} size="sm">
                                    <Text fontSize="sm" data-testid={`team-visibility-member-${member.id}`}>{member.name}</Text>
                                  </Checkbox>
                                ))}
                              </VStack>
                            </CheckboxGroup>
                          </Box>
                        )}
                        <FormErrorMessage>{errors.instant_task_members?.message}</FormErrorMessage>
                      </FormControl>
                    )}
                  </Box>
                );
              })}
            </VStack>
          </Box>
        )}

        <HStack justify="flex-end" spacing={3} pt={4}>
          {onCancel && (
            <Button variant="ghost" onClick={onCancel} data-testid="team-form-cancel-btn">
              Cancel
            </Button>
          )}
          <Button
            type="submit"
            colorScheme="brand"
            isLoading={isSubmitting}
            loadingText="Saving..."
            data-testid="team-form-submit-btn"
          >
            {initialData ? 'Update Member' : 'Add Member'}
          </Button>
        </HStack>
      </VStack>
    </form>
  );
}